import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import isNil from 'lodash/isNil';
import get from 'lodash/get';
import { compose, withProps } from 'recompose';

import { Paragraph } from 'components/core/Typography';
import ReviewRating from './ReviewRating';
import ReviewTitle from './ReviewTitle';
import ReviewText from './ReviewText';
import ReviewRatingDetails, { ratingDetailsType } from './ReviewRatingDetails';

import './review.scss';

export const enhance = compose(
	withProps((props) => {
		const reviewSchema = {
			'@context': 'http://schema.org/',
			'@type': 'Review',
			itemReviewed: {
				'@type': 'SelfStorage',
				image: props.images,
				name: props.facilityName,
				address: props.address
			},
			reviewRating: {
				'@type': 'Rating',
				ratingValue: props.reviewRating
			},
			name: props.reviewTitle,
			author: {
				'@type': 'Person',
				name: props.reviewerName
			},
			reviewBody: props.reviewText,
			publisher: {
				'@type': 'Organization',
				name: 'SpareFoot',
				address:props.address
			}
		};

		const stringifiedReviewSchema = JSON.stringify(reviewSchema);

		return { reviewSchema: stringifiedReviewSchema };
	})
);

export function Review(props) {
	return (
		<li id={`review-${props.id}`} className={classnames(props.className, 'review')}>
			<div className="review-content">
				<ReviewRating
					reviewDate={props.reviewDate}
					reviewRating={props.reviewRating}
					reviewRatingOutOf={props.reviewRatingOutOf}
				/>
				<ReviewTitle
					reviewTitle={props.reviewTitle}
				/>
				<ReviewText
					reviewText={props.reviewText}
				/>
				<ReviewRatingDetails
					recommended={props.recommended}
					priceRating={props.priceRating}
					serviceRating={props.serviceRating}
					cleanlinessRating={props.cleanlinessRating}
				/>
				<Paragraph last>
					- {props.reviewerName}
				</Paragraph>
			</div>
			{!isNil(props.managerResponse) &&
				<div className="manager-response">
					<ReviewRating
						reviewDate={new Date(get(props.managerResponse, 'date.format.iso'))}
					/>
					<Paragraph>
						{props.managerResponse.message}
					</Paragraph>
					<Paragraph last>
						- {props.managerResponse.title}
					</Paragraph>
				</div>
			}
			{props.reviewSchema &&
				<script
					key="schema"
					type="application/ld+json"
					// eslint-disable-next-line react/no-danger
					dangerouslySetInnerHTML={{
						__html: props.reviewSchema
					}}
				/>
			}
		</li>
	);
}

Review.propTypes = {
	className: PropTypes.string,
	/** Name of the reviewer */
	reviewerName: PropTypes.string.isRequired,
	/** Title of the review */
	reviewTitle: PropTypes.string,
	/** The actual review */
	reviewText: PropTypes.string,
	/** The date of the review */
	reviewDate: PropTypes.instanceOf(Date),
	/** The rating of the review */
	reviewRating: PropTypes.number.isRequired,
	/** What the rating is out of */
	reviewRatingOutOf: PropTypes.number,
	priceRating: ratingDetailsType,
	serviceRating: ratingDetailsType,
	cleanlinessRating: ratingDetailsType,
	recommended: PropTypes.bool,
	id: PropTypes.string,
	managerResponse: PropTypes.object,
	reviewSchema: PropTypes.string
};

export default enhance(Review);
